/* eslint-disable no-param-reassign */
import produce from 'immer';
import { readUpTo } from './chatState';

const CHAT_SET_DIALOG = 'CHAT_SET_DIALOG';
const CHAT_SET_ACTIVE = 'CHAT_SET_ACTIVE';
const CHAT_SET_ENABLED = 'CHAT_SET_ENABLED';
const CHAT_SET_ATTACHMENTS = 'CHAT_SET_ATTACHMENTS';
const CHAT_MESSAGE_INCOMING = 'CHAT_MESSAGE_INCOMING';
const CHAT_READ = 'CHAT_READ';
const CHAT_VIEWING = 'CHAT_VIEWING';
const CHAT_NOTICE_CLOSE = 'CHAT_NOTICE_CLOSE';

// Смяната на темата от координатора и готовият отговор след нея.
const systemEvent = { autoAnswer: 7, categoryChanged: 9 };

const setChatDialog = payload => ({
  type: CHAT_SET_DIALOG,
  payload,
});

const openTaskChatDialog = task =>
  setChatDialog({
    open: true,
    scheduleTaskId: task.id,
    title: [task.client, task.address].filter(Boolean).join(', '),
  });

const setActiveChats = payload => ({
  type: CHAT_SET_ACTIVE,
  payload,
});

// null — още не е проверено (ChatSwitch); false — изключен или недостъпен.
const setChatEnabled = enabled => ({
  type: CHAT_SET_ENABLED,
  payload: enabled,
});

// null — снимките са изключени (няма папка за тях в сървиса); иначе лимитите им.
const setChatAttachments = attachments => ({
  type: CHAT_SET_ATTACHMENTS,
  payload: attachments,
});

// Ново съобщение от координатор или колега в активен чат — червено, докато не се види, и известие (pop-up).
const chatMessageIncoming = message => ({
  type: CHAT_MESSAGE_INCOMING,
  payload: message,
});

// Докъде това устройство е показало чата — сървърът го научава до 1,5 сек. по-късно (receipts).
const chatRead = (chatId, messageId) => ({
  type: CHAT_READ,
  payload: { chatId, messageId },
});

// Чатът, който техникът гледа в момента (отворен прозорец, таблетът на преден план) — за него известие не излиза.
const chatViewing = chatId => ({
  type: CHAT_VIEWING,
  payload: chatId,
});

const closeChatNotice = kind => ({
  type: CHAT_NOTICE_CLOSE,
  payload: kind,
});

const reducer = (
  state = {
    chatDialog: {
      open: false,
      scheduleTaskId: null,
      title: '',
    },
    activeChats: [],
    readUpTo: {},
    viewingChatId: null,
    // Известията (функционалното описание, т. 4.2.1 и 4.2.2): за ново съобщение и за смяна на темата от координатора.
    messageNotice: null,
    topicNotice: null,
    enabled: null,
    attachments: null,
  },
  action
) =>
  produce(state, draft => {
    switch (action.type) {
      case CHAT_SET_DIALOG:
        draft.chatDialog = action.payload;
        break;

      case CHAT_SET_ACTIVE:
        draft.activeChats = action.payload;
        break;

      case CHAT_SET_ENABLED:
        draft.enabled = action.payload;
        if (!action.payload) {
          draft.activeChats = [];
          draft.chatDialog.open = false;
          draft.messageNotice = null;
          draft.topicNotice = null;
        }
        break;

      case CHAT_SET_ATTACHMENTS:
        draft.attachments = action.payload;
        break;

      case CHAT_MESSAGE_INCOMING: {
        const message = action.payload;
        const chat = draft.activeChats.find(x => x.id === message.chatId);
        if (!chat || message.id <= (chat.lastIncomingMessageId || 0)) break;
        // Всичко досега е видяно — броенето започва отначало.
        const seen = readUpTo(draft, chat) >= (chat.lastIncomingMessageId || 0);
        chat.unreadCount = (seen ? 0 : chat.unreadCount || 0) + 1;
        chat.lastIncomingMessageId = message.id;

        const notice = {
          chatId: chat.id,
          messageId: message.id,
          scheduleTaskId: chat.scheduleTaskId,
        };
        // Смяната на темата излиза и когато чатът е отворен; готовият отговор след нея — не отделно.
        if (message.systemEvent === systemEvent.categoryChanged)
          draft.topicNotice = { ...notice, text: message.body };
        else if (message.systemEvent !== systemEvent.autoAnswer && chat.id !== draft.viewingChatId)
          draft.messageNotice = {
            ...notice,
            senderName: message.senderName,
            senderType: message.senderType,
            text: message.body,
            hasAttachments: !!(message.attachments && message.attachments.length),
          };
        break;
      }

      case CHAT_READ: {
        const { chatId, messageId } = action.payload;
        draft.readUpTo[chatId] = Math.max(draft.readUpTo[chatId] || 0, messageId);
        const notice = draft.messageNotice;
        if (notice && notice.chatId === chatId && notice.messageId <= messageId)
          draft.messageNotice = null;
        break;
      }

      case CHAT_VIEWING:
        draft.viewingChatId = action.payload;
        if (draft.messageNotice && draft.messageNotice.chatId === action.payload)
          draft.messageNotice = null;
        break;

      case CHAT_NOTICE_CLOSE:
        draft[action.payload === 'topic' ? 'topicNotice' : 'messageNotice'] = null;
        break;

      default:
        break;
    }
  });

export {
  reducer,
  setChatDialog,
  openTaskChatDialog,
  setActiveChats,
  setChatEnabled,
  setChatAttachments,
  chatMessageIncoming,
  chatRead,
  chatViewing,
  closeChatNotice,
};
